import { Primitive } from '@radix-ui/react-primitive';
import { constant } from 'lodash';
import { type ComponentPropsWithoutRef, memo } from 'react';
import { type DragObjectWithType, type DropTargetMonitor, useDragLayer, useDrop } from 'react-dnd';

import { cn } from '@air/utils-tailwind';

import type DragType from '~/components/Shared/Drag/dragTypes';
import { type DropData, type DropLocation } from '~/components/Shared/Drag/dragTypes';

export type DropRearrangeAreaProps<T extends DragObjectWithType> = ComponentPropsWithoutRef<typeof Primitive.div> & {
  dropLocation: DropLocation;
  onItemRearrange: (draggedItem: T) => void;
  canDropItem?: (draggedItem: T) => boolean;
  dragTypes: DragType[];
  side: 'top' | 'bottom';
};

function collectDropData<T extends DragObjectWithType>(
  canDropItem: DropRearrangeAreaProps<T>['canDropItem'] = constant(true),
) {
  return (monitor: DropTargetMonitor): { isOver: boolean } => {
    const isOver = monitor.isOver({ shallow: false });
    const item = monitor.getItem();

    return {
      isOver: isOver && item && canDropItem(item),
    };
  };
}

/**
 * This component is used to handle rearranging boards and workspaces in navigation
 * As well as rearranging versions in the asset modal
 * It displays horizontal line if dragged item is over it
 */
const _DNDRearrangeArea = <T extends DragObjectWithType>({
  className,
  canDropItem = constant(true),
  dragTypes,
  dropLocation,
  onItemRearrange,
  side,
  ...restOfProps
}: DropRearrangeAreaProps<T>) => {
  const onDrop = (item: T): DropData => {
    const canDrop = canDropItem(item);

    if (canDrop) {
      onItemRearrange(item);

      return {
        type: 'rearrange',
        location: dropLocation,
      };
    }

    // Return rejected type to ensure drag state is properly cleared
    return {
      type: 'rejected',
      location: dropLocation,
    };
  };

  const { isDragging } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
  }));

  const [{ isOver }, drop] = useDrop({
    accept: dragTypes,
    collect: collectDropData(canDropItem),
    canDrop: (item: T) => canDropItem(item),
    drop: onDrop,
  });

  return (
    <Primitive.div
      className={cn(
        'absolute z-40 flex h-2 w-full items-center before:h-1 before:w-full before:rounded-24 before:content-[""] data-[dnd-dragging=true]:pointer-events-auto data-[side=top]:-top-1 data-[side=bottom]:-bottom-1 data-[dnd-over=true]:before:bg-blue-9',
        className,
      )}
      data-dnd-dragging={isDragging}
      data-dnd-over={isOver}
      data-side={side}
      {...restOfProps}
      ref={drop}
    />
  );
};

export const DNDRearrangeArea = memo(_DNDRearrangeArea) as typeof _DNDRearrangeArea;
